/**
 * Laabha — billing / credits client.
 *
 * Mirrors the contract used by secretary-pwa (dev-yes.axonbos.com):
 *
 *   GET  /api/v1/billing/balance               → { credits_balance, plan, currency, ... }
 *   GET  /api/v1/billing/transactions?limit=…  → { transactions: [...] }
 *   POST /api/v1/billing/topup  {credits}      → { order_id, key_id, amount, currency }
 *   POST /api/v1/billing/verify_payment        → { ok: true, credits_added }
 *
 * Balance is read in this priority order (matches AppShell/Settings logic):
 *     credits_balance → credits → credits_inr → balance → 0
 *
 * Recharge always uses Razorpay (loaded dynamically from CDN), minimum ₹49.
 */

import type { Envelope } from '@/types/chat';
import { authHeader } from './auth';

export interface BalanceData {
  /** Primary balance field — always read this first. */
  credits_balance?: number;
  credits?: number;
  credits_inr?: number;
  balance?: number;
  currency?: 'INR' | 'USD' | string;
  plan?: string; // 'regular' | 'managed' | 'premium' …
  /** Server may include low-balance warning copy. */
  low_balance?: boolean;
  /** Free credits remaining (for usage-cap UX). */
  free_credits?: number;
}

export interface BalanceTransaction {
  id: string;
  type: 'topup' | 'usage' | 'refund' | 'bonus';
  amount: number; // signed: positive = credit, negative = debit
  description?: string;
  created_at: string;
  metadata?: Record<string, unknown>;
}

export interface TopupOrder {
  order_id: string;
  key_id: string;
  amount: number; // in paise (smallest currency unit)
  currency: 'INR' | 'USD' | string;
  receipt?: string;
}

class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function unwrap<T>(resp: Response): Promise<T> {
  if (resp.status === 401) {
    // Don't clearAuth — defer to fetchMe-based verification.
    throw new ApiError('AUTH_REQUIRED', 'Authorization failed for this request.', 401);
  }
  if (!resp.ok && resp.status !== 422) {
    let txt = '';
    try {
      txt = await resp.text();
    } catch {}
    throw new ApiError(
      `HTTP_${resp.status}`,
      txt.slice(0, 200) || resp.statusText,
      resp.status,
    );
  }
  const body = (await resp.json()) as Envelope<T>;
  if (!body.success) {
    throw new ApiError(
      body.error?.code ?? 'UNKNOWN',
      body.error?.message ?? 'Request failed',
      resp.status,
    );
  }
  if (body.data == null) {
    throw new ApiError('EMPTY_DATA', 'Envelope data null', resp.status);
  }
  return body.data;
}

async function billingFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Gateway mounts billing_bll at /v1/billing (NOT /api/v1/billing).
  const resp = await fetch(`/v1/billing${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...authHeader(),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  return unwrap<T>(resp);
}

export const billingApi = {
  getBalance: () => billingFetch<BalanceData>('/balance'),

  getTransactions: (limit = 20) =>
    billingFetch<{ transactions: BalanceTransaction[] }>(
      `/transactions?limit=${limit}`,
    ),

  /** amount_inr in whole rupees (e.g. 500 for ₹500). */
  initiateTopup: (amount_inr: number) =>
    billingFetch<TopupOrder>('/topup', {
      method: 'POST',
      body: JSON.stringify({ credits: amount_inr }),
    }),

  verifyPayment: (
    payment_id: string,
    order_id: string,
    signature: string,
  ) =>
    billingFetch<{ ok: boolean; credits_added?: number }>('/verify_payment', {
      method: 'POST',
      body: JSON.stringify({
        razorpay_payment_id: payment_id,
        razorpay_order_id: order_id,
        razorpay_signature: signature,
      }),
    }),
};

/**
 * Extract the spendable rupee balance from the BalanceData envelope.
 * Used everywhere a balance number is displayed.
 */
export function readBalance(data: BalanceData | null | undefined): number {
  if (!data) return 0;
  return (
    data.credits_balance ??
    data.credits ??
    data.credits_inr ??
    data.balance ??
    0
  );
}

export function isBalanceDepleted(data: BalanceData | null | undefined): boolean {
  if (!data) return false;
  return readBalance(data) <= 0;
}

export function currencySymbol(data: BalanceData | null | undefined): string {
  return data?.currency === 'USD' ? '$' : '₹';
}

export function formatBalance(
  value: number,
  currency: string | undefined = 'INR',
): string {
  const symbol = currency === 'USD' ? '$' : '₹';
  return `${symbol}${value.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// ── Razorpay loader ──────────────────────────────────────────────────────

declare global {
  interface Window {
    Razorpay?: new (opts: RazorpayOptions) => RazorpayInstance;
  }
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  order_id: string;
  name: string;
  description?: string;
  image?: string;
  theme?: { color?: string };
  prefill?: { name?: string; email?: string; contact?: string };
  modal?: { ondismiss?: () => void };
  handler: (response: {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
  }) => void;
}

interface RazorpayInstance {
  open: () => void;
  close: () => void;
}

const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
let razorpayLoadPromise: Promise<boolean> | null = null;

export function loadRazorpay(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayLoadPromise) return razorpayLoadPromise;
  razorpayLoadPromise = new Promise<boolean>((resolve) => {
    const s = document.createElement('script');
    s.src = RAZORPAY_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => {
      razorpayLoadPromise = null;
      resolve(false);
    };
    document.body.appendChild(s);
  });
  return razorpayLoadPromise;
}

export { ApiError };
